import React, { useMemo, useState } from 'react';

// mui
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';

// mui-icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// component
import CustomTooltip from 'components/CustomTooltip';

interface CategoryNode {
  name: string;
  value: string;
  categories: CategoryNode[];
}

interface SingleSelectTreeListProps {
  title: string;
  tree: CategoryNode[];
  clear: () => void;
  dataTestId: string;
  value: string;
  setValue: (val: string) => void;
  titleTooltipMsg?: string;
  defaultExpanded: boolean;
  sort: boolean;
}

const TreeItemLabel = ({ name, isSelected }: { name: string; isSelected: boolean }) => (
  <Box display="flex" alignItems="center">
    <Typography
      variant="body2"
      className="notranslate"
      title={name}
      sx={{
        fontWeight: isSelected ? 'bold' : 'normal',
        letterSpacing: '0.235px',
        '&:hover': {
          fontWeight: 'bold',
          letterSpacing: 0,
        },
      }}
    >
      {name}
    </Typography>
  </Box>
);

const renderTree = (
  nodes: CategoryNode[],
  onSelect: (val: string) => void,
  selectedValue: string = '',
  level: number = 0
): React.ReactNode => {
  let currentValue = '';
  if (selectedValue && selectedValue?.includes(':')) {
    const parts = selectedValue.split(':');
    if (parts[1]) {
      const subParts = parts[1].split(' > ');
      currentValue = subParts[level] || '';
    }
  }

  return nodes.map((node) => (
    <TreeItem
      key={node.value}
      itemID={node.value}
      nodeId={node.value}
      sx={{
        [`& .MuiTreeItem-content`]: {
          backgroundColor: 'transparent !important',
          '&.Mui-selected': {
            backgroundColor: 'transparent !important',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent !important',
          },
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
        },
        '& .MuiTreeItem-group': {
          marginLeft: 1 * 0.5,
        },
        '& .MuiTreeItem-iconContainer': {
          marginRight: 0,
        },
      }}
      data-Testid={`single-select-tree-list-item-lvl-${level}-${node.name.replaceAll(' ', '')}`}
      label={
        <Box
          sx={{
            padding: '0.3rem 0.1rem',
            borderRadius: 0,
          }}
          onClick={() => {
            onSelect(node.value);
          }}
          data-Testid={`single-select-tree-list-lbl-lvl-${level}-${node.name.replaceAll(' ', '')}`}
        >
          <TreeItemLabel name={node.name} isSelected={currentValue === node.name} />
        </Box>
      }
    >
      {node.categories && renderTree(node.categories, onSelect, selectedValue, level + 1)}
    </TreeItem>
  ));
};

const SingleSelectTreeList = ({
  title,
  tree,
  clear,
  dataTestId,
  value,
  setValue,
  titleTooltipMsg,
  defaultExpanded = false,
  sort = false,
}: SingleSelectTreeListProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const [isExpandedOptions, setIsExpandedOptions] = useState<boolean>(false);

  const handleClear = () => {
    clear();
  };

  const handleOptionChange = (newValue: string) => {
    const newSelected = value === newValue ? null : newValue;
    setValue(newSelected);
  };

  const filteredOptions = useMemo(() => {
    let result = tree;
    if (sort) {
      result = result.sort((a: any, b: any) => b.count - a.count);
    }
    return result;
  }, [tree, sort]);

  const displayedOptions = useMemo(() => {
    return isExpandedOptions ? filteredOptions : filteredOptions.slice(0, 5);
  }, [filteredOptions, isExpandedOptions]);

  return (
    <Box
      sx={{
        width: '100%',
      }}
      data-testid={`single-select-tree-list-${dataTestId}-container`}
    >
      <input
        type="hidden"
        value={value || ''}
        data-testid={`single-select-tree-list-${dataTestId}-hidden-input`}
        aria-hidden="true"
      />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderBottom: '1px solid #ccc', padding: 0.5, backgroundColor: '#F9FAFB', width: '100%' }}
      >
        <Box sx={{ padding: '0 0.5rem' }}>
          {titleTooltipMsg && (
            <CustomTooltip
              title={titleTooltipMsg}
              dataTestId={`single-select-tree-list-${dataTestId}-title-tooltip`}
              placement="top"
            />
          )}
          <Typography sx={{ fontWeight: 'bold' }} data-testid={`single-select-tree-list-${dataTestId}-title`}>
            {title}
          </Typography>
        </Box>

        <div>
          <IconButton
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid={`single-select-tree-list-${dataTestId}-expand`}
            aria-label={`single-select-tree-list-${dataTestId}-expand`}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      </Box>
      <Collapse
        in={isExpanded}
        sx={{
          padding: '0 0.5rem',
          backgroundColor: 'white',
        }}
      >
        {isExpanded && (
          <Box data-testid={`single-select-tree-list-${dataTestId}-container-expanded`}>
            <Box
              sx={{
                maxHeight: isExpandedOptions ? '240px' : 'auto',
                overflowY: isExpandedOptions ? 'auto' : 'visible',
                overflowX: 'hidden',
                paddingTop: '0.5rem',
              }}
            >
              <TreeView
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                defaultExpanded={displayedOptions?.[0]?.value ? [displayedOptions[0].value] : []}
              >
                {renderTree(displayedOptions, handleOptionChange, value)}
              </TreeView>
            </Box>
            <Box display="flex" justifyContent="space-between" sx={{ padding: '0.5rem 0.5rem 1rem 0.5rem' }}>
              {filteredOptions.length > 5 && (
                <Typography
                  variant="body2"
                  sx={{
                    cursor: 'pointer',
                    color: 'black',
                    textDecoration: 'underline',
                    width: '100%',
                  }}
                  onClick={() => setIsExpandedOptions((prev) => !prev)}
                  data-testid={`single-select-tree-list-${dataTestId}-label-${
                    isExpandedOptions ? 'show-less' : 'show-more'
                  }`}
                >
                  {isExpandedOptions ? 'Show less' : 'Show all'}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  cursor: 'pointer',
                  color: 'black',
                  textDecoration: 'underline',
                  textAlign: 'end',
                  width: '100%',
                }}
                onClick={handleClear}
                data-testid={`single-select-tree-list-${dataTestId}-label-clear`}
              >
                Clear
              </Typography>
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default SingleSelectTreeList;
